/**
 * sharepoint.js — Wysupp SharePoint sync via Microsoft Graph + MSAL
 * App: Wy-Expenses (dc4eb159-f248-41f8-aaaa-f917e87397e9)
 * Site: wysupp.sharepoint.com/sites/TalentManagement
 * Library: Documentos
 */

const CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID || 'dc4eb159-f248-41f8-aaaa-f917e87397e9';
const TENANT_ID = process.env.REACT_APP_AZURE_TENANT_ID || '474a05a4-2fea-4153-aa28-adb7c909841c';
const SHAREPOINT_HOST = 'wysupp.sharepoint.com';
const SITE_PATH = '/sites/TalentManagement';
const LIBRARY_NAME = 'Documentos';

// Graph API site/drive IDs (pre-fetched)
const SITE_ID = '98cce0a5-1bff-4103-9284-8b4674b2b027';
const DRIVE_LIST_ID = 'a3613385-a52a-4d48-89fd-bdaff4deeab1';

let msalInstance = null;

// ── Load MSAL and initialize ──────────────────────────────────────────────────
function loadMsal() {
  return new Promise((resolve, reject) => {
    if (window.msal) { resolve(); return; }
    const script = document.createElement('script');
    script.src = 'https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

async function getMsalInstance() {
  if (msalInstance) return msalInstance;
  await loadMsal();
  const config = {
    auth: {
      clientId: CLIENT_ID,
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
  };
  msalInstance = new window.msal.PublicClientApplication(config);
  await msalInstance.initialize();
  return msalInstance;
}

// ── Get access token ──────────────────────────────────────────────────────────
async function getAccessToken() {
  const msal = await getMsalInstance();
  const scopes = [
    `https://${SHAREPOINT_HOST}/AllSites.Write`,
    'Files.ReadWrite.All',
    'Sites.ReadWrite.All',
    'User.Read',
  ];

  const accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    try {
      const result = await msal.acquireTokenSilent({ scopes, account: accounts[0] });
      return result.accessToken;
    } catch (e) {
      // Silent failed, try popup
    }
  }
  const result = await msal.acquireTokenPopup({ scopes });
  return result.accessToken;
}

// ── Build CSV ─────────────────────────────────────────────────────────────────
function buildCSV(report) {
  const expenses = report.expenses || [];
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const billable = expenses.filter(e => e.billable).reduce((s, e) => s + e.amount, 0);
  const reimbursable = expenses.filter(e => e.reimbursable).reduce((s, e) => s + e.amount, 0);
  const balance = (report.cashAdvance || 0) - total;
  const esc = v => `"${String(v || '').replace(/"/g, '""')}"`;

  const rows = [
    ['WYSUPP EXPENSE REPORT'],
    ['Report:', esc(report.name), 'Consultant:', esc(report.userName), 'Date:', new Date().toLocaleDateString()],
    ['Project:', esc(report.project), 'Currency:', report.currency || 'USD'],
    [],
    ['#', 'Date', 'Description', 'Type', 'Project', 'Currency', 'Amount', 'Billable', 'Reimbursable', 'Notes'],
    ...expenses.map((e, i) => [
      i + 1, e.date, esc(e.title), e.type, esc(e.project),
      e.currency || report.currency, e.amount.toFixed(2),
      e.billable ? 'Yes' : 'No', e.reimbursable ? 'Yes' : 'No', esc(e.notes || ''),
    ]),
    [],
    ['SUMMARY'],
    ['Total Expenses', '', '', '', '', report.currency, total.toFixed(2)],
    ['Billable Amount', '', '', '', '', report.currency, billable.toFixed(2)],
    ['Reimbursable Amount', '', '', '', '', report.currency, reimbursable.toFixed(2)],
    ['Cash Advance', '', '', '', '', report.currency, (report.cashAdvance || 0).toFixed(2)],
    ['Balance (Advance - Total)', '', '', '', '', report.currency, balance.toFixed(2)],
  ];
  return rows.map(r => r.join(',')).join('\n');
}

// ── Upload file via Graph API ─────────────────────────────────────────────────
async function uploadToGraph(token, folderPath, filename, content, contentType = 'text/csv') {
  const encodedPath = folderPath.split('/').map(encodeURIComponent).join('/');
  const url = `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_HOST}:${SITE_PATH}:/drives/${DRIVE_LIST_ID}/root:/${encodedPath}/${encodeURIComponent(filename)}:/content`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': contentType,
    },
    body: content,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Upload failed: ${res.status}`);
  }
  const data = await res.json();
  return data.webUrl;
}

// ── Main sync function ────────────────────────────────────────────────────────
export async function syncReportToSharePoint(report) {
  const token = await getAccessToken();

  // Build folder structure: Wy-Expenses/2026-04/ConsultantName/ReportName
  const date = new Date();
  const monthFolder = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  const consultantName = (report.userName || 'Unknown').replace(/[^a-zA-Z0-9_\- ]/g, '_');
  const reportName = (report.name || 'Report').replace(/[^a-zA-Z0-9_\- ]/g, '_');
  const folderPath = `Wy-Expenses/${monthFolder}/${consultantName}/${reportName}`;

  // Upload CSV summary
  const csvContent = buildCSV(report);
  const csvFilename = `${date.toISOString().split('T')[0]}_${consultantName}_Summary.csv`;
  const csvUrl = await uploadToGraph(token, folderPath, csvFilename, csvContent, 'text/csv');

  // Upload receipt photos
  const expenses = report.expenses || [];
  let receiptsUploaded = 0;
  for (let i = 0; i < expenses.length; i++) {
    const exp = expenses[i];
    if (exp.receipt && exp.receipt.startsWith('data:image')) {
      try {
        const [meta, b64] = exp.receipt.split(',');
        const contentType = meta.match(/:(.*?);/)[1];
        const ext = contentType.split('/')[1] || 'jpg';
        const binary = atob(b64);
        const arr = new Uint8Array(binary.length);
        for (let j = 0; j < binary.length; j++) arr[j] = binary.charCodeAt(j);
        const blob = new Blob([arr], { type: contentType });
        const receiptName = `${String(i + 1).padStart(2, '0')}_${(exp.title || 'receipt').replace(/[^a-zA-Z0-9]/g, '_')}.${ext}`;
        await uploadToGraph(token, folderPath, receiptName, blob, contentType);
        receiptsUploaded++;
      } catch (e) {
        console.log('Receipt upload failed:', e);
      }
    }
  }

  return {
    syncedAt: new Date().toISOString(),
    fileUrl: csvUrl,
    receiptsUploaded,
    folderPath,
  };
}

// ── Background sync ───────────────────────────────────────────────────────────
export async function registerBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('wy-expenses-sync');
    } catch (e) {
      console.log('Background sync not supported:', e);
    }
  }
}

export function onBackgroundSyncMessage(callback) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'BACKGROUND_SYNC') callback();
    });
  }
}

export function initMsal() {}